import React, { useState, useEffect } from 'react';
import { systemService } from '../services';
import { DataTable } from '../components';
import {
  useSideDialog,
  useModalDialog,
  useGlobalState,
  usePrincipal,
  useMessage,
  useStatus,
} from '../hooks';
import { handleError, Role, Asset } from '../helpers';

export const Systems = () => {
  const [systems, setSystems] = useState([]);
  const [loggedInUser] = useGlobalState('loggedInUser');
  const [statuses] = useGlobalState('statuses');
  const [settings] = useGlobalState('settings');
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const clearSelectedItems = () => {
    setSelectedItems([]);
  };

  const { showMessage } = useMessage();
  const { showSideDialog, hideSideDialog, setSideDialog } = useSideDialog(clearSelectedItems);
  const { showModalDialog, hideModalDialog } = useModalDialog();
  const { fetchPrincipals } = usePrincipal();
  const { appendStatus } = useStatus({ setState: setSystems, assetCategory: Asset.System });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    const promise = new Promise(resolve => {
      systemService
        .getAll()
        .then(res => {
          setSystems(res.systems);
          appendStatus();
          setLoading(false);
          resolve(res.systems);
        })
        .catch(err => {
          showMessage(err, 'error');
          setLoading(false);
        });
    });

    Promise.all([promise, fetchFieldStatistics()])
      .then(result => {
        updateFieldStatistics(result[0], result[1]);
        fetchPrincipals(result[0], setSystems);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchFieldStatistics = async () => {
    return new Promise(resolve => {
      systemService
        .getFieldStatistics()
        .then(res => {
          resolve(res.field_statistics);
        })
        .catch(() => {
          return null;
        });
    });
  };

  const updateFieldStatistics = (data, statistics) => {
    let statistic = {};
    let maxValue = 0;

    statistics.forEach(row => {
      if (row.dataset_count > maxValue) {
        maxValue = row.dataset_count;
      }
    });

    setSystems(prev =>
      prev.map(item => {
        statistic = statistics.find(x => x.system_id === item.system_id);
        return {
          ...item,
          dataset_count: statistic ? statistic.dataset_count : 0,
          attribute_ratio: statistic ? statistic.attribute_ratio : 0,
          dataset_count_max: maxValue,
        };
      })
    );
  };

  const addSystem = async data => {
    const systems = { systems: Array.isArray(data) ? data : [data] };

    await systemService
      .create(systems)
      .then(async res => {
        setSideDialog(prev => ({ ...prev, formErrors: {} }));
        fetchItems();
        showMessage(res.message);
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editSystem = async data => {
    const systems = { systems: Array.isArray(data) ? data : [data] };

    await systemService
      .update(systems)
      .then(async res => {
        fetchItems();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const deleteSystem = async data => {
    await systemService
      .destroy({ systems: data })
      .then(async res => {
        fetchItems();
        hideModalDialog();
        clearSelectedItems();
        showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const editable = loggedInUser && loggedInUser.user_role_name === Role.admin;

  return (
    <div className="Systems wide-margin">
      <DataTable
        columns={[
          {
            id: 'system_name',
            name: 'Name',
            link: '/systems/:system_id',
            tooltip: 'system_description',
            className: 'bold',
          },
          { id: 'system_code', name: 'Code' },
          { id: 'system_type', name: 'Type', className: 'small-text' },
          {
            id: 'system_description',
            name: 'Description',
            type: 'description',
            viewMode: 'descriptions',
          },
          {
            id: 'dataset_count',
            name: 'Datasets',
            type: 'chart',
            chartType: 'bar',
            data: { data: 'dataset_count', max: 'dataset_count_max' },
            viewMode: 'statistics',
          },
          {
            id: 'attribute_ratio',
            name: 'Documentation Level',
            type: 'chart',
            chartType: 'percent-bar',
            data: { data: 'attribute_ratio' },
            viewMode: 'statistics',
          },
          ...(settings.SHOW_SYSTEM_STATUSES
            ? [
                {
                  id: 'status_name',
                  name: 'Status',
                  type: 'pill',
                  value: 'status_id',
                  values: statuses,
                  tooltip: 'status_description',
                },
              ]
            : []),
          {
            id: 'principal_name',
            name: 'Owner',
            type: 'owner',
            link: '/:principal_url',
            data: { photo: 'principal_photo' },
            tooltipSubtitle: 'principal_type_name',
          },
        ]}
        buttons={[
          {
            label: '+ Add',
            action: 'add',
            mainButton: true,
            onClick: items => showSideDialog('addSystem', items || selectedItems, addSystem),
          },
          {
            label: 'Edit',
            action: 'edit',
            onClick: items => showSideDialog('editSystem', items || selectedItems, editSystem),
          },
          {
            label: 'Delete',
            action: 'delete',
            onClick: items => showModalDialog('deleteSystem', items || selectedItems, deleteSystem),
          },
        ]}
        title="Systems"
        data={systems}
        idColumn="system_id"
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        loading={loading}
        editable={editable}
        filterable={true}
        filterObjects={[
          'system_name',
          'system_code',
          'system_description',
          'system_type',
          ...(settings.SHOW_SYSTEM_STATUSES ? ['status_name'] : []),
          'principal_name',
        ]}
        showViewModes={true}
      />
    </div>
  );
};
